import { Fragment, useState } from 'react';
import { ErrorBoundary } from '@toss/error-boundary';
import { SwitchCase } from '@toss/react';
import { logger } from '@zep/core-logger';
import { type AllPeriodsQuizsetRankingSummaryRes } from '@/api';
import { ErrorBoundaryFallback } from '@/components';
import { type FeaturedPeriod } from '@/module/featured/featuedQuiz.const';
import { useFetchFeaturedQuizzesSummary } from '@/module/featured/hook/useFetchFeaturedQuizzesSummary';
import { FeaturedEmpty } from '@/module/featured/shared/FeaturedQuizzesAside/FeaturedEmpty';
import { usePublicCategoryFilter } from '@/module/public/hooks';
import { NewMapBanner } from '../../components/NewMapBanner';
import { FeaturedHeader } from './FeaturedHeader';
import { FeaturedQuizCard } from './FeaturedQuizCard';
interface FeaturedQuizzesProps {
  schoolType?: string;
}
export const FeaturedQuizzesAside = (props: FeaturedQuizzesProps) => {
  return <ErrorBoundary onError={e => logger.captureError(e, {
    fingerprint: 'FeaturedQuizzes'
  })} renderFallback={args => <ErrorBoundaryFallback {...args} />} data-sentry-element="ErrorBoundary" data-sentry-component="FeaturedQuizzesAside" data-sentry-source-file="FeaturedQuizzesAside.tsx">
      <FeaturedQuizzesComponent schoolType={props.schoolType} data-sentry-element="FeaturedQuizzesComponent" data-sentry-source-file="FeaturedQuizzesAside.tsx" />
    </ErrorBoundary>;
};
const FeaturedQuizzesComponent = (props: FeaturedQuizzesProps) => {
  const [selectedPeriod, setSelectedPeriod] = useState<FeaturedPeriod>('DAILY');
  const {
    state: {
      schoolType: schoolTypePublic
    }
  } = usePublicCategoryFilter();
  const schoolType = props.schoolType || schoolTypePublic;
  const {
    data,
    isLoading
  } = useFetchFeaturedQuizzesSummary({
    schoolType: schoolType
  });
  const quizzesData = data?.data?.[selectedPeriod.toLowerCase() as keyof AllPeriodsQuizsetRankingSummaryRes] ?? [];
  const status = isLoading ? 'LOADING' : quizzesData.length > 0 ? 'SUCCESS' : 'EMPTY';
  return <div className={'flex w-full flex-col items-start gap-lg self-stretch'} data-sentry-component="FeaturedQuizzesComponent" data-sentry-source-file="FeaturedQuizzesAside.tsx">
      <NewMapBanner data-sentry-element="NewMapBanner" data-sentry-source-file="FeaturedQuizzesAside.tsx" />
      <div className="flex w-full flex-col gap-xs">
        <FeaturedHeader schoolType={schoolType} activeTab={selectedPeriod} onClickTabButton={tab => {
        setSelectedPeriod(tab);
        logger.track('featured_tab_click', {
          period: tab,
          schoolType: schoolType
        });
      }} data-sentry-element="FeaturedHeader" data-sentry-source-file="FeaturedQuizzesAside.tsx" />
        <div className={'flex w-full flex-col items-start self-stretch px-xs'}>
          <SwitchCase caseBy={{
          LOADING: null,
          EMPTY: <FeaturedEmpty />,
          SUCCESS: <Fragment>
                  {quizzesData.map((quiz, index, all) => <Fragment key={quiz.quizsetId}>
                      <FeaturedQuizCard key={selectedPeriod + quiz.quizsetId} {...quiz} period={selectedPeriod} schoolType={schoolType} />
                      {all.length - 1 !== index && <Border />}
                    </Fragment>)}
                </Fragment>
        }} value={status} data-sentry-element="SwitchCase" data-sentry-source-file="FeaturedQuizzesAside.tsx" />
        </div>
      </div>
    </div>;
};
const Border = () => <div className={'my-3xs w-full border-t-[1px] border-assistive'} data-sentry-component="Border" data-sentry-source-file="FeaturedQuizzesAside.tsx" />;